import { Chip, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogChangeDestinationAddresses from './DialogChangeDestinationAddresses';
import { EditLocationAlt, Place } from '@mui/icons-material';

const MultipleAddressSelector = ({
  value: destinationAddresses = [],
  onChange,
  state,
  recipients,
}) => {
  const { t } = useTranslation('translation');
  const [openChangeDestinationAddresses, setOpenChangeDestinationAddresses] =
    useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const uniqueRecipientTeamIds = Array.from(
    new Set(
      Object.values(state.foodTypes || {})
        .flatMap((value) => value.assignedRecipients || [])
        .filter((recipient) => recipient.teamId !== 'unassigned')
        .map((recipient) => recipient.teamId)
    )
  );

  const mapDestinationAddresses = (recipient) => {
    if (!recipient) return {};
    return {
      lat: recipient.lat,
      lng: recipient.lng,
      formattedAddress: recipient.formatted_address,
      name: recipient.place,
      streetAddress: recipient.street_address,
      city: recipient.city,
      province: recipient.province,
      country: recipient.country,
      zipCode: recipient.zip_code,
      placeId: recipient.place_id,
      timeZoneId: recipient.timezone_id,
    };
  };

  useEffect(() => {
    const updatedDestinationAddresses = uniqueRecipientTeamIds.map((teamId) => {
      const recipient = recipients.find((r) => r.team_id === teamId);

      const customDestinationAddress = destinationAddresses.find(
        (item) => item.teamId === teamId
      );
      if (customDestinationAddress) {
        return {
          teamId,
          location: customDestinationAddress.location,
        };
      }
      return {
        teamId,
        location: mapDestinationAddresses(recipient),
      };
    });
    onChange(updatedDestinationAddresses);
  }, [
    JSON.stringify(uniqueRecipientTeamIds),
    JSON.stringify(destinationAddresses),
  ]);

  return (
    <>
      {openChangeDestinationAddresses && selectedRecipient && (
        <DialogChangeDestinationAddresses
          open={openChangeDestinationAddresses}
          onClose={() => setOpenChangeDestinationAddresses(false)}
          onChange={(value) => {
            let newDestinationAddresses = [...destinationAddresses];
            const index = newDestinationAddresses.findIndex(
              (item) => item.teamId === selectedRecipient.team_id
            );
            newDestinationAddresses[index] = {
              teamId: selectedRecipient.team_id,
              location: value,
            };
            onChange(newDestinationAddresses);
          }}
          value={
            destinationAddresses.find(
              (item) => item.teamId === selectedRecipient.team_id
            )?.location || mapDestinationAddresses(selectedRecipient)
          }
        />
      )}
      <Stack flexWrap="wrap" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t('donations.change-destination-addresses')}
        </Typography>
        <Stack direction="row" flexWrap="wrap" alignItems="start" gap={2}>
          {recipients.length > 0
            ? uniqueRecipientTeamIds.map((teamId) => {
                const recipient = recipients.find((r) => r.team_id === teamId);
                const customDestinationAddress = destinationAddresses.find(
                  (item) => item.teamId === teamId
                );

                const isCustomAddress =
                  recipient.lat !== customDestinationAddress?.location?.lat ||
                  recipient.lng !== customDestinationAddress?.location?.lng;

                return (
                  <Chip
                    key={teamId}
                    square
                    label={recipient.name}
                    icon={
                      isCustomAddress ? (
                        <EditLocationAlt
                          sx={{
                            fontSize: '20px',
                          }}
                        />
                      ) : (
                        <Place
                          sx={{
                            fontSize: '20px',
                          }}
                        />
                      )
                    }
                    variant={isCustomAddress ? 'contained' : 'outlined'}
                    color="default"
                    onClick={() => {
                      setSelectedRecipient(recipient);
                      setOpenChangeDestinationAddresses(true);
                    }}
                  />
                );
              })
            : null}
        </Stack>
      </Stack>
    </>
  );
};

export default MultipleAddressSelector;
